/** Shared meta description length rules (SSR + client + CMS). */

export const SEO_MIN_DESCRIPTION_LENGTH = 25;
export const SEO_MAX_DESCRIPTION_LENGTH = 160;

const SHORT_FILLERS = [
  ' Learn more at American Police Training.',
  ' Browse training events and courses nationwide.',
];

const TRAILING_DESCRIPTION_FRAGMENTS = [
  /\s+on American Police$/i,
  /\s+American Police$/i,
  /\s+on American$/i,
  /\s+on$/i,
  /\s+and$/i,
  /\s+the$/i,
  /\s+of$/i,
];

function stripTrailingDescriptionFragments(text: string): string {
  let out = text.replace(/[,;:\-–—]\s*$/u, '').trim();
  let previous = '';
  while (out !== previous) {
    previous = out;
    for (const pattern of TRAILING_DESCRIPTION_FRAGMENTS) {
      out = out.replace(pattern, '').trim();
    }
    out = out.replace(/[,;:\-–—]\s*$/u, '').trim();
  }
  return out;
}

/**
 * Shorten a meta description to `max` characters.
 * Prefer a sentence end when one lands at 70 characters or more; otherwise cut on a word
 * and drop a trailing stop word or a partial brand so the text does not end "on American".
 */
export function truncateMetaDescription(
  text: string,
  max: number = SEO_MAX_DESCRIPTION_LENGTH,
): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean || clean.length <= max) return clean;
  const head = clean.slice(0, max);
  let sentenceEnd = -1;
  const sentence = /[.!?](?=\s|$)/g;
  let match: RegExpExecArray | null;
  while ((match = sentence.exec(head))) {
    const end = match.index + 1;
    if (end >= 70 && end <= max) sentenceEnd = end;
  }
  if (sentenceEnd >= 70) {
    return clean.slice(0, sentenceEnd).trim();
  }
  let cut = head;
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace >= 70) cut = cut.slice(0, lastSpace);
  cut = stripTrailingDescriptionFragments(cut);
  if (!/[.!?]$/.test(cut)) {
    if (cut.length >= max) {
      cut = stripTrailingDescriptionFragments(cut.slice(0, Math.max(0, max - 1)));
    }
    cut = `${cut}.`;
  }
  if (cut.length > max) {
    cut = stripTrailingDescriptionFragments(cut.slice(0, Math.max(0, max - 1)));
    if (!/[.!?]$/.test(cut)) cut = `${cut}.`;
  }
  return cut;
}

export function ensureMetaDescriptionLength(description: string): string {
  const clean = description.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  if (
    clean.length >= SEO_MIN_DESCRIPTION_LENGTH &&
    clean.length <= SEO_MAX_DESCRIPTION_LENGTH
  ) {
    return clean;
  }
  if (clean.length > SEO_MAX_DESCRIPTION_LENGTH) {
    const out = truncateMetaDescription(clean, SEO_MAX_DESCRIPTION_LENGTH);
    if (out.length >= SEO_MIN_DESCRIPTION_LENGTH) return out;
    return truncateMetaDescription(clean, SEO_MAX_DESCRIPTION_LENGTH);
  }
  if (clean.length > 0) {
    let padded = clean;
    for (const filler of SHORT_FILLERS) {
      if (padded.length >= SEO_MIN_DESCRIPTION_LENGTH) break;
      const remaining = SEO_MAX_DESCRIPTION_LENGTH - padded.length;
      if (remaining <= 0) break;
      padded += filler.substring(0, remaining);
    }
    if (padded.length >= SEO_MIN_DESCRIPTION_LENGTH) {
      return padded;
    }
  }
  return (
    'American Police Training helps law enforcement professionals find training events, courses, and instructors across the U.S.'
  );
}
